import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  IconButton,
  LinearProgress,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import BlockIcon from '@mui/icons-material/Block';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FlagIcon from '@mui/icons-material/Flag';
import EventIcon from '@mui/icons-material/Event';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import BoltIcon from '@mui/icons-material/Bolt';
import type { ClientShareView, ShareFile, ShareTrack, ShareUpdate, UpdatesPage } from '../api/publicShare';
import { ProgressChart } from './ProgressChart';
import { GanttChart } from './gantt/GanttChart';
import { formatDate, humanizeStatus, milestoneEffectiveDate, statusColor } from '../lib/format';
import { topRunningTasks } from '../lib/runningTasks';
import type { DailyLog, ProjectTrack, TrackItem, TrackStatus } from '../api/types';

/**
 * Resolves a progress photo to a renderable src. Treated as a hook (call it at
 * the top of a component): the public share page returns a plain URL, the
 * client portal fetches the photo with the bearer token and returns a blob
 * object-URL (null while loading).
 */
export type UsePhotoSrc = (photoId: string) => string | null;

/**
 * Fetches one page of the updates feed. Differs per surface (public share token
 * vs. authed portal), so it's injected like `usePhotoSrc`. Newest-first.
 */
export type FetchUpdatesPage = (page: number, pageSize: number) => Promise<UpdatesPage>;

/**
 * The read-only client-facing project overview — hero, stat cards, timeline,
 * work streams, milestones, updates feed. Rendered by both the public share
 * page (/share/:token) and the authenticated client portal (/portal), always
 * from the same sanitized ClientShareView payload (no money, no internals).
 */
/** Trigger a download for a client-visible document (auth differs by surface). */
export type OnDownloadFile = (file: ShareFile) => void;

export function ProjectOverviewReport({
  data,
  usePhotoSrc,
  fetchUpdatesPage,
  updatesSurfaceKey,
  onDownloadFile,
  stickyTop,
}: {
  data: ClientShareView;
  usePhotoSrc: UsePhotoSrc;
  /** Pages the "Latest updates" feed straight from the server (full history). */
  fetchUpdatesPage: FetchUpdatesPage;
  /** Stable per-surface key so the feed's react-query cache doesn't collide. */
  updatesSurfaceKey: string;
  onDownloadFile?: OnDownloadFile;
  /** Offset (px/CSS) at which the stat bar sticks — pass the app-bar height in
   *  the portal so it pins just below it; omit (0) on the bare share page. */
  stickyTop?: number | string;
}) {
  return (
    <Box sx={{ bgcolor: 'grey.100', pb: 8 }}>
      <Hero data={data} />
      <Container maxWidth="md" sx={{ mt: -6 }}>
        <Stack gap={3}>
          <StatCards data={data} stickyTop={stickyTop} />
          <RunningNowSection data={data} />
          <TimelineSection data={data} />
          <Section title="Work streams" icon={<TrendingUpIcon fontSize="small" />}>
            <Stack gap={2}>
              {data.tracks.length === 0 && (
                <Typography color="text.secondary">Planning in progress.</Typography>
              )}
              {data.tracks.map((t) => (
                <TrackCard key={t.id} track={t} updates={data.updates} />
              ))}
            </Stack>
          </Section>
          {data.deliveryMilestones.length > 0 && (
            <Section title="Milestones" icon={<FlagIcon fontSize="small" />}>
              <MilestoneTimeline milestones={data.deliveryMilestones} />
            </Section>
          )}
          {data.files?.length > 0 && onDownloadFile && (
            <Section title="Documents" icon={<FolderOpenIcon fontSize="small" />}>
              <DocumentsList files={data.files} onDownload={onDownloadFile} />
            </Section>
          )}
          <Section title="Latest updates" icon={<EventIcon fontSize="small" />}>
            <UpdatesFeed
              usePhotoSrc={usePhotoSrc}
              tracks={data.tracks}
              fetchPage={fetchUpdatesPage}
              surfaceKey={updatesSurfaceKey}
            />
          </Section>
          <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
            This is a live report — it updates automatically as work is logged.
            {data.clientName ? ` Prepared for ${data.clientName}.` : ''}
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}

export function OverviewLoadingState() {
  return (
    <Box sx={{ bgcolor: 'grey.100' }}>
      <Skeleton variant="rectangular" height={220} />
      <Container maxWidth="md" sx={{ mt: -6 }}>
        <Stack gap={2}>
          <Skeleton variant="rounded" height={96} />
          <Skeleton variant="rounded" height={180} />
          <Skeleton variant="rounded" height={180} />
        </Stack>
      </Container>
    </Box>
  );
}

// ── Layout pieces ────────────────────────────────────────────────────────────

function Hero({ data }: { data: ClientShareView }) {
  const theme = useTheme();
  const { project } = data;
  const dateRange = [formatDate(project.startDate), formatDate(project.expectedEndDate)]
    .filter((d) => d !== '—')
    .join(' → ');

  return (
    <Box
      sx={{
        color: 'common.white',
        pt: { xs: 4, md: 6 },
        pb: 10,
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.primary.light} 100%)`,
      }}
    >
      <Container maxWidth="md">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={3}
        >
          <Box>
            <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: 2 }}>
              Project progress report
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
              {project.name}
            </Typography>
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
              <Chip
                size="small"
                label={humanizeStatus(project.status)}
                sx={{
                  color: 'common.white',
                  bgcolor: alpha(theme.palette.common.white, 0.18),
                  fontWeight: 600,
                }}
              />
              {dateRange && (
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  {dateRange}
                </Typography>
              )}
            </Stack>
            {project.description && (
              <Typography variant="body2" sx={{ opacity: 0.75, mt: 1, maxWidth: 520 }}>
                {project.description}
              </Typography>
            )}
          </Box>
          <ProgressRing pct={data.overallPct} />
        </Stack>
      </Container>
    </Box>
  );
}

function ProgressRing({ pct }: { pct: number }) {
  const theme = useTheme();
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={110}
        thickness={4}
        sx={{ color: alpha(theme.palette.common.white, 0.25) }}
      />
      <CircularProgress
        variant="determinate"
        value={pct}
        size={110}
        thickness={4}
        sx={{ color: 'common.white', position: 'absolute', left: 0 }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="h5" fontWeight={800} lineHeight={1}>
          {pct}%
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          complete
        </Typography>
      </Box>
    </Box>
  );
}

function StatCards({ data, stickyTop = 0 }: { data: ClientShareView; stickyTop?: number | string }) {
  const tracksDone = data.tracks.filter((t) => t.status === 'complete').length;
  const msDone = data.deliveryMilestones.filter((m) => m.status === 'complete').length;
  const daysLeft = remainingDays(data.project.expectedEndDate);

  const stats: Array<{ label: string; value: string }> = [
    { label: 'Overall progress', value: `${data.overallPct}%` },
    { label: 'Work streams done', value: `${tracksDone}/${data.tracks.length}` },
  ];
  if (data.deliveryMilestones.length > 0) {
    stats.push({ label: 'Milestones reached', value: `${msDone}/${data.deliveryMilestones.length}` });
  }
  if (data.project.actualEndDate) {
    stats.push({ label: 'Delivered', value: formatDate(data.project.actualEndDate) });
  } else if (daysLeft !== null) {
    stats.push({
      label: daysLeft >= 0 ? 'Days to target' : 'Days past target',
      value: `${Math.abs(daysLeft)}`,
    });
  }

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        position: 'sticky',
        top: stickyTop,
        zIndex: (theme) => theme.zIndex.appBar - 1,
      }}
    >
      <Stack direction="row" divider={<Box sx={{ borderLeft: 1, borderColor: 'divider' }} />}>
        {stats.map((s) => (
          <Box key={s.label} sx={{ flex: 1, p: { xs: 1.5, sm: 2.5 }, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight={700}>
              {s.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {s.label}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 1.5, color: 'text.secondary' }}>
        {icon}
        <Typography variant="subtitle1" fontWeight={700} color="text.primary">
          {title}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
}

// ── Running now ──────────────────────────────────────────────────────────────

/**
 * "Currently working on" — the top 3 sub-tasks actively underway, so the client
 * sees what's happening right now at a glance. Same selector as the internal
 * Overview tab; hidden entirely when nothing is in progress (no empty box).
 */
function RunningNowSection({ data }: { data: ClientShareView }) {
  const running = useMemo(
    () => topRunningTasks(data.tracks, data.timeline.criticalPath),
    [data.tracks, data.timeline.criticalPath],
  );
  if (running.length === 0) return null;

  return (
    <Section title="Currently working on" icon={<BoltIcon fontSize="small" />}>
      <Paper elevation={1} sx={{ borderRadius: 3, p: 2.5 }}>
        <Stack gap={2}>
          {running.map((t) => {
            const pct = Math.min(100, Math.max(0, t.progressPct));
            return (
              <Box key={t.id}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {t.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {t.trackLabel}
                      {t.scheduledEnd ? ` · target ${formatDate(t.scheduledEnd)}` : ''}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0 }}>
                    {pct}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  color={t.status === 'late' ? 'error' : 'primary'}
                  sx={{ mt: 1, height: 8, borderRadius: 4 }}
                />
              </Box>
            );
          })}
        </Stack>
      </Paper>
    </Section>
  );
}

// ── Timeline (Gantt) ─────────────────────────────────────────────────────────

/**
 * The same read-only Gantt the internal Overview tab renders — track summary
 * bars, item bars with pace tint, baseline ghosts, dependency arrows, critical
 * path and holiday shading. Hidden entirely until anything has planned dates
 * (the internal empty-state copy talks about the Plan tab, which a client
 * can't see).
 */
function TimelineSection({ data }: { data: ClientShareView }) {
  const ganttTracks = useMemo(() => data.tracks.map(toGanttTrack), [data.tracks]);
  const hasDates = data.tracks.some(
    (t) =>
      (t.plannedStart && t.plannedEnd) ||
      t.items.some((i) => (i.scheduledStart ?? i.plannedStart) && (i.scheduledEnd ?? i.plannedEnd)),
  );
  if (!hasDates) return null;

  return (
    <Section title="Timeline" icon={<ViewTimelineIcon fontSize="small" />}>
      <GanttChart
        tracks={ganttTracks}
        dependencies={data.dependencies}
        criticalPath={data.timeline.criticalPath}
        holidays={data.timeline.holidays}
        milestones={data.deliveryMilestones.map((m) => ({
          id: m.id,
          name: m.name,
          // "graph wins": a linked milestone sits at its engine-computed date —
          // same resolution as the internal hub, so the share timeline matches it.
          date: m.scheduledDate ?? m.dueDate,
          done: m.status === 'complete',
          dueDate: m.dueDate,
          delayReason: m.delayReason,
        }))}
        milestoneLinks={data.milestoneLinks}
      />
      {data.timeline.projectFinish && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          Projected finish: {formatDate(data.timeline.projectFinish)}
        </Typography>
      )}
    </Section>
  );
}

function toGanttTrack(t: ShareTrack): ProjectTrack {
  return {
    ...toChartTrack(t),
    items: t.items.map(
      (i): TrackItem => ({
        id: i.id,
        trackId: t.id,
        name: i.name,
        sequence: i.sequence,
        totalUnits: i.totalUnits,
        currentUnits: i.currentUnits,
        progressPct: i.progressPct,
        status: i.status as TrackStatus,
        computedStatus: i.status as TrackStatus,
        plannedStart: i.plannedStart,
        plannedEnd: i.plannedEnd,
        scheduledStart: i.scheduledStart,
        scheduledEnd: i.scheduledEnd,
        baselineStart: i.baselineStart,
        baselineEnd: i.baselineEnd,
      }),
    ),
  };
}

// ── Work streams ─────────────────────────────────────────────────────────────

function TrackCard({ track, updates }: { track: ShareTrack; updates: ShareUpdate[] }) {
  const [open, setOpen] = useState(false);
  // Weighted-by-size roll-up from the backend; fall back to the track counter.
  const pct =
    track.progressPct ??
    (track.totalUnits > 0 ? Math.round((track.currentUnits / track.totalUnits) * 100) : 0);
  const hasItems = track.items.length > 0;
  const target = track.plannedEnd ? ` · target ${formatDate(track.plannedEnd)}` : '';
  const trackUpdates = useMemo(
    () => updates.filter((u) => u.trackId === track.id),
    [updates, track.id],
  );
  const hasDetail = track.items.length > 0 || trackUpdates.length > 0;

  return (
    <Paper elevation={1} sx={{ borderRadius: 3, p: 2.5 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ cursor: hasDetail ? 'pointer' : 'default' }}
        onClick={hasDetail ? () => setOpen((v) => !v) : undefined}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography fontWeight={600} noWrap>
            {track.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {hasItems
              ? `${track.items.length} sub-task${track.items.length === 1 ? '' : 's'}${target}`
              : `${track.currentUnits}/${track.totalUnits} ${track.unitLabel}${target}`}
          </Typography>
        </Box>
        <Stack direction="row" gap={1} alignItems="center" flexShrink={0}>
          <Chip
            size="small"
            label={humanizeStatus(track.status)}
            color={trackChipColor(track.status)}
            variant="outlined"
          />
          <Typography fontWeight={700}>{pct}%</Typography>
          {hasDetail && (
            <ExpandMoreIcon
              sx={{
                color: 'text.secondary',
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform 150ms',
              }}
            />
          )}
        </Stack>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={track.status === 'late' ? 'error' : pct === 100 ? 'success' : 'primary'}
        sx={{ mt: 1.5, height: 8, borderRadius: 4 }}
      />
      {hasDetail && (
        <Collapse in={open} unmountOnExit>
          <Box sx={{ mt: 2 }}>
            {track.items.length > 0 && (
              <Stack gap={0.75} sx={{ mb: trackUpdates.length > 0 ? 2 : 0 }}>
                {track.items.map((i) => {
                  const done = i.totalUnits > 0 && i.currentUnits >= i.totalUnits;
                  const itemPct =
                    i.progressPct ??
                    (i.totalUnits > 0 ? Math.round((i.currentUnits / i.totalUnits) * 100) : 0);
                  return (
                    <Stack key={i.id} direction="row" alignItems="center" gap={1}>
                      {done ? (
                        <CheckCircleIcon fontSize="small" color="success" />
                      ) : (
                        <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'grey.400' }} />
                      )}
                      <Typography variant="body2" sx={{ flex: 1 }} color={done ? 'text.secondary' : 'text.primary'}>
                        {i.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {`${itemPct}%`}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            )}
            {trackUpdates.length > 0 && (
              <ProgressChart track={toChartTrack(track)} logs={toChartLogs(trackUpdates)} />
            )}
          </Box>
        </Collapse>
      )}
    </Paper>
  );
}

/** Adapt the sanitized public shapes to what the shared ProgressChart expects. */
function toChartTrack(t: ShareTrack): ProjectTrack {
  return {
    id: t.id,
    projectId: '',
    type: t.type,
    label: t.label,
    unitLabel: t.unitLabel,
    totalUnits: t.totalUnits,
    currentUnits: t.currentUnits,
    progressPct: t.progressPct,
    status: t.status as TrackStatus,
    dueDate: t.dueDate,
    plannedStart: t.plannedStart,
    plannedEnd: t.plannedEnd,
    createdAt: '',
    updatedAt: '',
    items: [],
  };
}

function toChartLogs(updates: ShareUpdate[]): DailyLog[] {
  return updates.map((u) => ({
    id: u.id,
    trackId: u.trackId,
    reportedById: '',
    reportedDate: u.reportedDate,
    progressIncrement: u.progressIncrement,
    isBlocked: u.isBlocked,
    note: u.note,
    hasProblem: false,
    reportedAt: u.reportedDate,
  }));
}

function trackChipColor(status: string) {
  return statusColor(status === 'complete' ? 'delivered' : status === 'late' ? 'overdue' : status);
}

// ── Milestones ───────────────────────────────────────────────────────────────

function MilestoneTimeline({ milestones }: { milestones: ClientShareView['deliveryMilestones'] }) {
  const theme = useTheme();
  return (
    <Paper elevation={1} sx={{ borderRadius: 3, p: 2.5 }}>
      <Stack>
        {milestones.map((m, idx) => {
          const done = m.status === 'complete';
          const late = m.status === 'late';
          const { date: msDate, movedFrom } = milestoneEffectiveDate(m);
          return (
            <Stack key={m.id} direction="row" gap={1.5}>
              <Stack alignItems="center">
                {done ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ color: late ? 'error.main' : 'grey.400' }} />
                )}
                {idx < milestones.length - 1 && (
                  <Box
                    sx={{
                      width: 2,
                      flex: 1,
                      minHeight: 24,
                      bgcolor: done ? alpha(theme.palette.success.main, 0.4) : 'grey.300',
                      my: 0.5,
                    }}
                  />
                )}
              </Stack>
              <Box sx={{ pb: idx < milestones.length - 1 ? 2.5 : 0 }}>
                <Typography fontWeight={600} variant="body2">
                  {m.name}
                </Typography>
                <Typography variant="caption" color={late ? 'error' : 'text.secondary'}>
                  {done
                    ? `Completed ${formatDate(m.completedDate)}`
                    : `${late ? 'Was due' : 'Due'} ${formatDate(msDate)}`}
                  {!done && movedFrom ? ` · moved from ${formatDate(movedFrom)}` : ''}
                </Typography>
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
}

// ── Documents ────────────────────────────────────────────────────────────────

const FILE_CATEGORY_LABEL: Record<string, string> = {
  manual: 'Manual Book',
  wiring_diagram: 'Wiring Diagram',
  plc_program: 'PLC Program',
  layout: 'Panel Layout',
  other: 'Document',
};

function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DocumentsList({ files, onDownload }: { files: ShareFile[]; onDownload: OnDownloadFile }) {
  return (
    <Paper elevation={1} sx={{ borderRadius: 3, p: 1 }}>
      <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
        {files.map((f) => (
          <Stack key={f.id} direction="row" gap={1.5} alignItems="center" sx={{ p: 1.5 }}>
            <InsertDriveFileIcon color="action" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap title={f.originalName}>
                {f.originalName}
              </Typography>
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                <Chip size="small" variant="outlined" label={FILE_CATEGORY_LABEL[f.category] ?? 'Document'} />
                <Typography variant="caption" color="text.secondary">
                  {fileSize(f.sizeBytes)}
                </Typography>
              </Stack>
              {f.description && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {f.description}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={() => onDownload(f)} aria-label={`Download ${f.originalName}`}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

// ── Updates feed ─────────────────────────────────────────────────────────────

const UPDATES_PAGE_SIZE = 25;

function UpdatesFeed({
  usePhotoSrc,
  tracks,
  fetchPage,
  surfaceKey,
}: {
  usePhotoSrc: UsePhotoSrc;
  tracks: ShareTrack[];
  fetchPage: FetchUpdatesPage;
  surfaceKey: string;
}) {
  const [lightbox, setLightbox] = useState<{ src: string; caption: string | null } | null>(null);
  const [page, setPage] = useState(1);
  const trackLabel = useMemo(
    () => new Map(tracks.map((t) => [t.id, t.label])),
    [tracks],
  );

  // Server-paginated: each page is its own query, previous page stays on screen
  // while the next loads (keepPreviousData) so paging doesn't flash empty.
  const { data, isLoading, isPlaceholderData } = useQuery({
    queryKey: ['updates-feed', surfaceKey, page, UPDATES_PAGE_SIZE],
    queryFn: () => fetchPage(page, UPDATES_PAGE_SIZE),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / UPDATES_PAGE_SIZE));

  // Switching project (portal) starts the feed over at page 1.
  useEffect(() => {
    setPage(1);
  }, [surfaceKey]);

  // If the feed shrinks under us (e.g. a refetch with fewer rows), step back.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  if (isLoading) {
    return (
      <Stack gap={1.5}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={88} sx={{ borderRadius: 3 }} />
        ))}
      </Stack>
    );
  }

  if (total === 0) {
    return (
      <Paper elevation={1} sx={{ borderRadius: 3, p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">No updates yet — work starts soon.</Typography>
      </Paper>
    );
  }

  const pageItems = data?.items ?? [];
  const start = (page - 1) * UPDATES_PAGE_SIZE;

  return (
    <Stack gap={1.5} sx={{ opacity: isPlaceholderData ? 0.6 : 1, transition: 'opacity 120ms' }}>
      {pageItems.map((u) => (
        <Paper key={u.id} elevation={1} sx={{ borderRadius: 3, p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {formatDate(u.reportedDate)} · {trackLabel.get(u.trackId) ?? 'Project'}
            </Typography>
            {u.isBlocked ? (
              <Chip size="small" color="warning" icon={<BlockIcon />} label="On hold" variant="outlined" />
            ) : (
              u.progressIncrement > 0 && (
                <Chip size="small" color="success" variant="outlined" label={`+${u.progressIncrement}`} />
              )
            )}
          </Stack>
          {u.note && <Typography variant="body2">{u.note}</Typography>}
          {u.photos.length > 0 && (
            <Stack direction="row" gap={1} flexWrap="wrap" mt={1}>
              {u.photos.map((p) => (
                <FeedPhoto
                  key={p.id}
                  photoId={p.id}
                  caption={p.caption}
                  usePhotoSrc={usePhotoSrc}
                  onOpen={(src) => setLightbox({ src, caption: p.caption })}
                />
              ))}
            </Stack>
          )}
        </Paper>
      ))}
      {pageCount > 1 && (
        <Stack alignItems="center" gap={0.5} mt={1}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
            size="small"
          />
          <Typography variant="caption" color="text.secondary">
            Showing {start + 1}–{start + pageItems.length} of {total}
          </Typography>
        </Stack>
      )}

      <Dialog open={Boolean(lightbox)} onClose={() => setLightbox(null)} maxWidth="lg">
        {lightbox && (
          <Box sx={{ position: 'relative', bgcolor: 'common.black' }}>
            <IconButton
              onClick={() => setLightbox(null)}
              sx={{ position: 'absolute', top: 8, right: 8, color: 'common.white', bgcolor: 'rgba(0,0,0,0.4)' }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box
              component="img"
              src={lightbox.src}
              alt={lightbox.caption ?? 'progress photo'}
              sx={{ display: 'block', maxWidth: '90vw', maxHeight: '85vh' }}
            />
            {lightbox.caption && (
              <Typography
                variant="body2"
                sx={{ color: 'common.white', p: 1.5, textAlign: 'center' }}
              >
                {lightbox.caption}
              </Typography>
            )}
          </Box>
        )}
      </Dialog>
    </Stack>
  );
}

/**
 * One feed thumbnail. `usePhotoSrc` is called here, at component top level, so
 * implementations that use hooks internally (the portal's authed blob fetch)
 * obey the rules of hooks — each photo gets its own component instance.
 */
function FeedPhoto({
  photoId,
  caption,
  usePhotoSrc,
  onOpen,
}: {
  photoId: string;
  caption: string | null;
  usePhotoSrc: UsePhotoSrc;
  onOpen: (src: string) => void;
}) {
  const src = usePhotoSrc(photoId);
  if (!src) {
    return <Skeleton variant="rounded" width={84} height={84} sx={{ borderRadius: 2 }} />;
  }
  return (
    <Box
      component="img"
      src={src}
      alt={caption ?? 'progress photo'}
      loading="lazy"
      onClick={() => onOpen(src)}
      sx={{
        width: 84,
        height: 84,
        objectFit: 'cover',
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'transform 120ms',
        '&:hover': { transform: 'scale(1.04)' },
      }}
    />
  );
}

// ── Utils ────────────────────────────────────────────────────────────────────

function remainingDays(expectedEnd: string | null): number | null {
  if (!expectedEnd) return null;
  const end = new Date(`${expectedEnd}T00:00:00`);
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((end.getTime() - today.getTime()) / 86_400_000);
}
